import {
  getFromBase64Csib,
  type CoordinatesClient,
} from '@data-access/coordinates';
import { getLogger } from '@trimble/logging';

const csibIdentityCache = new Map<string, Promise<CsibIdentity | undefined>>();

export interface CsibIdentity {
  systemName?: string | null;
  datumSystemId?: number;
  geoidSystemId?: number;
}

const DEFAULT_SYSTEM_NAME = 'Default';

export function resetCsibIdentityCache(): void {
  csibIdentityCache.clear();
}

/**
 * Match when systemName is a catalog name on both sides and equal.
 * datumSystemId / geoidSystemId only constrain when both ids are > 0.
 */
export function areCsibIdentitiesEquivalent(
  pointCloud: CsibIdentity | undefined,
  project: CsibIdentity | undefined
): boolean {
  if (pointCloud === undefined || project === undefined) {
    return false;
  }

  const pointCloudName = catalogSystemName(pointCloud.systemName);
  const projectName = catalogSystemName(project.systemName);
  if (
    pointCloudName === undefined ||
    projectName === undefined ||
    pointCloudName !== projectName
  ) {
    return false;
  }

  if (
    isCatalogId(pointCloud.datumSystemId) &&
    isCatalogId(project.datumSystemId) &&
    pointCloud.datumSystemId !== project.datumSystemId
  ) {
    return false;
  }

  if (
    isCatalogId(pointCloud.geoidSystemId) &&
    isCatalogId(project.geoidSystemId) &&
    pointCloud.geoidSystemId !== project.geoidSystemId
  ) {
    return false;
  }

  return true;
}

/**
 * Decode both CSIBs via Coordinates (cached per CSIB).
 * Returns undefined when lookup fails or either CSIB is unknown
 * (Default / missing catalog name) — caller should report no issue.
 * Not on the load path.
 */
export async function areCsibsEquivalentByIdentity(
  pointCloudCsib64: string,
  projectCsib64: string,
  coordinatesClient: CoordinatesClient
): Promise<boolean | undefined> {
  try {
    const [pointCloud, project] = await Promise.all([
      fetchCsibIdentity(pointCloudCsib64, coordinatesClient),
      fetchCsibIdentity(projectCsib64, coordinatesClient),
    ]);
    const pointCloudName = catalogSystemName(pointCloud?.systemName);
    const projectName = catalogSystemName(project?.systemName);
    if (pointCloudName === undefined || projectName === undefined) {
      return undefined;
    }
    return areCsibIdentitiesEquivalent(pointCloud, project);
  } catch (error) {
    getLogger().warn(
      'CSIB identity lookup failed; skipping mismatch status',
      undefined,
      error instanceof Error ? error : new Error(String(error))
    );
    return undefined;
  }
}

function toOptionalNumber(
  value: number | string | undefined
): number | undefined {
  if (value === undefined) {
    return undefined;
  }
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function catalogSystemName(
  name: string | null | undefined
): string | undefined {
  const trimmed = name?.trim();
  if (!trimmed || trimmed === DEFAULT_SYSTEM_NAME) {
    return undefined;
  }
  return trimmed;
}

function isCatalogId(id: number | undefined): id is number {
  return id !== undefined && id > 0;
}

function fetchCsibIdentity(
  csib64: string,
  coordinatesClient: CoordinatesClient
): Promise<CsibIdentity | undefined> {
  const cached = csibIdentityCache.get(csib64);
  if (cached !== undefined) {
    return cached;
  }

  const promise = (async (): Promise<CsibIdentity | undefined> => {
    const response = await getFromBase64Csib({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      query: { csib_64: csib64 },
      client: coordinatesClient,
    });
    if (response.error) {
      throw new Error(JSON.stringify(response.error));
    }
    const system = response.data?.coordinateSystem;
    if (!system) {
      return undefined;
    }
    return {
      systemName: system.systemName,
      datumSystemId: toOptionalNumber(
        system.datumSystemId ?? system.datumInfo?.datumSystemId
      ),
      geoidSystemId: toOptionalNumber(
        system.geoidSystemId ?? system.geoidInfo?.geoidSystemId
      ),
    };
  })();

  csibIdentityCache.set(csib64, promise);
  promise.catch(() => {
    csibIdentityCache.delete(csib64);
  });
  return promise;
}
